import React, { useState } from "react";
import emailjs from "emailjs-com";
import Footer from "../components/Footer/Footer";
import { useTranslation } from "react-i18next";
import { FaUser, FaEnvelope, FaPhone, FaComment, FaPaperPlane, FaCheckCircle } from "react-icons/fa";

const ContactUs = () => {
	const { t } = useTranslation();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);

	const [formData, setFormData] = useState({
		name: "",
		email: "",
		phone: "",
		message: "",
	});

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setIsSubmitting(true);
		emailjs
			.sendForm(
				process.env.REACT_APP_EMAILJS_SERVICE_ID,
				process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
				e.target,
				process.env.REACT_APP_EMAILJS_USER_ID
			)
			.then(
				(result) => {
					setIsSuccess(true);
					setFormData({
						name: "",
						email: "",
						phone: "",
						message: "",
					});
					setTimeout(() => setIsSuccess(false), 5000);
				},
				(error) => {
					alert(t("contactUs.failureMessage"));
				}
			)
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const inputFields = [
		{
			name: "name",
			type: "text",
			label: t("contactUs.nameLabel"),
			icon: FaUser,
			required: true,
		},
		{
			name: "email",
			type: "email",
			label: t("contactUs.emailLabel"),
			icon: FaEnvelope,
			required: true,
		},
		{
			name: "phone",
			type: "tel",
			label: `${t("contactUs.phoneLabel")} (${t("contactUs.optionalLabel")})`,
			icon: FaPhone,
			required: false,
		},
	];

	return (
		<>
			<section className="min-h-screen bg-gradient-to-br from-cream via-warm to-cream dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 dark:text-white py-20 relative overflow-hidden">
				{/* Background decorations */}
				<div className="absolute top-20 left-10 w-72 h-72 bg-accent/10 rounded-full blur-3xl animate-pulse-slow"></div>
				<div className="absolute bottom-20 right-10 w-96 h-96 bg-slate-400/10 rounded-full blur-3xl animate-pulse-slow animate-delay-300"></div>
				<div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-slate-400/5 to-accent/5 rounded-full blur-3xl"></div>

				<div className="container mx-auto px-6 md:px-12 relative z-10">
					{/* Header */}
					<div className="text-center mb-12 animate-fade-in-down">
						<span className="text-accent font-semibold text-sm tracking-wider">
							{t("contactUs.title")}
						</span>
						<h1 className="section-title mt-3">
							<span className="gradient-text">{t("contactUs.title")}</span>
						</h1>
						<p className="section-subtitle mt-4">
							{t("heroDescription")}
						</p>
					</div>

					{/* Form Container */}
					<div className="flex justify-center animate-fade-in-up animate-delay-200">
						<div className="w-full max-w-xl">
							{/* Success Message */}
							{isSuccess && (
								<div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-2xl flex items-center gap-3 animate-fade-in">
									<FaCheckCircle className="text-green-500 text-xl" />
									<span className="text-green-700 dark:text-green-300 font-medium">
										{t("contactUs.successMessage")}
									</span>
								</div>
							)}

							<form
								onSubmit={handleSubmit}
								className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-lg p-8 md:p-10 rounded-3xl shadow-card-hover border border-gray-100 dark:border-slate-700"
							>
								<div className="space-y-6">
									{inputFields.map((field, index) => (
										<div
											key={field.name}
											className="animate-fade-in-up"
											style={{ animationDelay: `${index * 100}ms` }}
										>
											<label
												htmlFor={field.name}
												className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2"
											>
												{field.label}
											</label>
											<div className="relative">
												<field.icon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
												<input
													type={field.type}
													name={field.name}
													id={field.name}
													value={formData[field.name]}
													onChange={handleChange}
													required={field.required}
													className="input-modern pl-12"
													placeholder={field.label.replace(" *", "")}
												/>
											</div>
										</div>
									))}

									{/* Message Field */}
									<div className="animate-fade-in-up animate-delay-300">
										<label
											htmlFor="message"
											className="block text-gray-700 dark:text-gray-300 text-sm font-semibold mb-2"
										>
											{t("contactUs.messageLabel")}
										</label>
										<div className="relative">
											<FaComment className="absolute left-4 top-4 text-gray-400 text-lg" />
											<textarea
												name="message"
												id="message"
												value={formData.message}
												onChange={handleChange}
												required
												className="input-modern pl-12 min-h-[150px] resize-none"
												rows="5"
												placeholder={t("contactUs.messageLabel")}
											></textarea>
										</div>
									</div>
								</div>

								{/* Submit Button */}
								<div className="mt-8 animate-fade-in-up animate-delay-400">
									<button
										type="submit"
										disabled={isSubmitting}
										className="w-full primary-btn text-lg py-4 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
									>
										{isSubmitting ? (
											<>
												<div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
												{t("contactUs.submitButton")}...
											</>
										) : (
											<>
												<FaPaperPlane />
												{t("contactUs.submitButton")}
											</>
										)}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</section>
			<Footer />
		</>
	);
};

export default ContactUs;
